import { useEffect, useState } from "react";

export interface DeezerArtist {
  id: number;
  name: string;
  picture_medium?: string;
  picture_big?: string;
  picture_xl?: string;
  link?: string;
  nb_fan?: number;
}

interface DeezerSearchResponse {
  data: DeezerArtist[];
}

/**
 * Search artists on Deezer (free, no API key, CORS-enabled).
 * Deezer artist links resolve to Spotify via Songlink with very high accuracy.
 */
export function useDeezerArtistSearch(query: string, limit = 6) {
  const [items, setItems] = useState<DeezerArtist[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setItems([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const t = setTimeout(async () => {
      try {
        // Proxy through edge function (Deezer API has no CORS headers)
        const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
        const url = `https://${projectId}.supabase.co/functions/v1/deezer-search?q=${encodeURIComponent(
          q,
        )}&limit=${limit}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Search ${res.status}`);
        const data = (await res.json()) as DeezerSearchResponse;
        if (cancelled) return;
        setItems(data.data ?? []);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Search failed");
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, limit]);

  return { items, loading, error };
}

/**
 * Find a Spotify artist by name using Spotify's official Search API
 * (proxied via edge function with Client Credentials). Returns the canonical
 * Spotify artist URL + high-res image, or null if no match.
 */
export async function findSpotifyArtistByName(name: string): Promise<{
  url: string;
  image: string | null;
  name: string;
} | null> {
  try {
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    const res = await fetch(
      `https://${projectId}.supabase.co/functions/v1/deezer-search?action=spotify-artist&name=${encodeURIComponent(
        name,
      )}`,
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.artist?.url) return null;
    return {
      url: data.artist.url,
      image: data.artist.image ?? null,
      name: data.artist.name,
    };
  } catch {
    return null;
  }
}
